import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';
import { getDeliveryLocations } from '@/api/sales/deliveryLocation';
import { getCampaigns } from '@/api/core/campaign';
import { getOrganisations } from '@/api/core/organisation';
import { registerQueueHandler } from '@/sync/offlineSyncQueue';

// Returned when offline with nothing cached yet (e.g. first-ever launch offline) — a neutral
// empty state, not placeholder data, since it can be shown to real users.
const EMPTY_POINTS = {
	total: 0,
	pending: 0,
	validated: 0,
	denied: 0,
	organization: null,
	rank: null,
	totalUsers: null,
	nextRank: null,
	season: null,
};

const CLAIM_STATUS = { 0: 'PENDING', 1: 'VALIDATED', 2: 'DENIED' };

const pointsCache = new CacheManager('VueCache', 'cache', {
	maxAge: 5 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'Points',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.points.`,
});

async function apiFetch(endpoint, options = {}) {
	const response = await authenticatedFetch(getApiUrl(endpoint), options, 'tokenSales');
	if (response.status === 404) return null;
	if (!response.ok) throw new Error(`HTTP ${response.status}`);
	return response.json();
}

function daysRemaining(endDate) {
	if (!endDate) return null;
	return Math.max(0, Math.round((new Date(endDate) - new Date()) / 86400000));
}

async function resolveOrganization() {
	const organisationId = iframeDataBridge.getCurrentUser()?.organisationId;
	if (!organisationId) return null;

	const organisations = await getOrganisations().catch(() => []);
	const own = (organisations ?? []).find((o) => o.id === organisationId);
	if (!own) return null;

	const parent = organisations.find((o) => o.id === own.parentId);
	return { parent: parent?.label ?? null, own: own.label ?? null };
}

function periodName(period) {
	if (!period) return null;
	const fmt = new Intl.DateTimeFormat('fr-FR', { month: 'short' });
	const start = new Date(period.startDate);
	const end = new Date(period.endDate);
	return `${fmt.format(start)} – ${fmt.format(end)} ${end.getFullYear()}`;
}

function resolvePromoName(claim, campaignsMap) {
	const campaign = campaignsMap.get(claim.campaignId);
	if (!campaign) return claim.comment?.trim() || `Réclamation #${claim.id}`;
	const offer = claim.campaignOfferId ? campaign.offers?.find((o) => o.id === claim.campaignOfferId) : null;
	return offer ? `${campaign.label} / ${offer.label}` : campaign.label;
}

function mapClaim(claim, dlMap = new Map(), campaignsMap = new Map()) {
	const status = CLAIM_STATUS[claim.status] ?? 'PENDING';
	const dl = dlMap.get(claim.deliveryLocationId);
	const locationLabel = claim.deliveryLocationId ? dl?.companyName || dl?.label || `PDL #${claim.deliveryLocationId}` : null;
	return {
		id: claim.id,
		date: claim.createdDate,
		type: claim.appointmentUid ? 'NEGOTIATION' : 'BONUS',
		campaignId: claim.campaignId,
		offerId: claim.campaignOfferId,
		appointmentUid: claim.appointmentUid,
		deliveryLocationId: claim.deliveryLocationId,
		promoName: resolvePromoName(claim, campaignsMap),
		location: locationLabel,
		pointsClaimed: claim.points,
		pointsValidated: status === 'VALIDATED' ? (claim.pointsValidated ?? null) : null,
		campaignOfferStartDate: claim.campaignOfferStartDate ?? null,
		campaignOfferEndDate: claim.campaignOfferEndDate ?? null,
		status,
		comment: claim.comment ?? '',
		deniedReason: status === 'DENIED' ? claim.comment?.trim() || null : null,
		validator: null,
		_raw: claim,
	};
}

// The legacy VM always builds these dates via `new Date(x).toISOString()` (millisecond precision,
// e.g. "...T23:00:00.000Z"), while the backend's own claim records often omit the milliseconds
// (e.g. "...T23:00:00Z") — same instant, different string. Comparing as strings made every
// classic-offer claim silently fail to match its own offer context. Compare by timestamp instead.
function sameInstant(a, b) {
	const ta = new Date(a).getTime();
	const tb = new Date(b).getTime();
	return !Number.isNaN(ta) && !Number.isNaN(tb) && ta === tb;
}

// Does a points-history entry belong to the given offer context? Every non-null field on
// `context` must match the entry. Catalog offers are identified by campaignId/offerId alone,
// but "pirate" (local, non-catalog) offers can share the same campaignId/offerId across
// distinct negotiations, so the negotiated period (campaignOfferStartDate/EndDate) is also
// compared whenever the context provides it, disambiguating those cases too.
export function matchesOfferContext(entry, context) {
	if (!context) return true;
	const { campaignId, offerId, campaignOfferStartDate, campaignOfferEndDate, deliveryLocationId } = context;

	if (campaignId != null && String(entry.campaignId ?? '') !== String(campaignId)) return false;
	if (offerId != null && String(entry.offerId ?? '') !== String(offerId)) return false;
	if (campaignOfferStartDate && !sameInstant(entry.campaignOfferStartDate, campaignOfferStartDate)) return false;
	if (campaignOfferEndDate && !sameInstant(entry.campaignOfferEndDate, campaignOfferEndDate)) return false;
	if (deliveryLocationId != null && String(entry.deliveryLocationId ?? '') !== String(deliveryLocationId)) return false;
	return true;
}

async function fetchOrCache(key, fetcher, fallback) {
	const memCached = pointsCache.getFromMemory(key);
	if (memCached) return memCached;

	const cached = await pointsCache.get(key);
	if (cached) {
		pointsCache.setInMemory(key, cached);
		return cached;
	}

	if (!iframeDataBridge.isOnline()) {
		log('info', 'Points', `📴 Offline: returning fallback for ${key}`);
		return fallback;
	}

	try {
		const data = await fetcher();
		await pointsCache.put(key, data);
		pointsCache.setInMemory(key, data);
		log('success', 'Points', `✅ Fetched ${key}`);
		return data;
	} catch (error) {
		log('warn', 'Points', `⚠️ API not available for ${key} - returning fallback: ${error.message}`);
		return fallback;
	}
}

export async function getUserPoints(forceRefresh = false) {
	// Only bypass the cache when we can actually replace it — offline, clearing it would just
	// destroy the last-known-good totals and fall through to the empty fallback below.
	if (forceRefresh && iframeDataBridge.isOnline()) {
		pointsCache.clearMemory();
		await pointsCache.clear();
	}
	return fetchOrCache(
		'userPoints',
		async () => {
			const userId = iframeDataBridge.getCurrentUser()?.id;
			const claimsPath = userId ? `Core/Scores/Claims?userId=${userId}` : 'Core/Scores/Claims';
			const [score, period, claims, organization] = await Promise.all([apiFetch('Core/Scores/Current'), apiFetch('Core/Scores/Periods/Current'), apiFetch(claimsPath), resolveOrganization()]);

			const allClaims = Array.isArray(claims) ? claims : [];
			const periodClaims = period ? allClaims.filter((c) => c.scorePeriodId === period.id) : allClaims;
			const pending = periodClaims.filter((c) => c.status === 0).reduce((s, c) => s + c.points, 0);
			const validated = periodClaims.filter((c) => c.status === 1).reduce((s, c) => s + (c.pointsValidated ?? 0), 0);
			const denied = periodClaims.filter((c) => c.status === 2).reduce((s, c) => s + c.points, 0);

			return {
				total: score?.points || validated || 0,
				pending,
				validated,
				denied,
				organization,
				season: period ? { id: period.id, name: periodName(period), daysRemaining: daysRemaining(period.endDate), startDate: period.startDate ?? null, endDate: period.endDate ?? null } : null,
			};
		},
		EMPTY_POINTS
	);
}

export async function getPointsHistory(forceRefresh = false) {
	if (forceRefresh && iframeDataBridge.isOnline()) {
		pointsCache.clearMemory();
		await pointsCache.clear();
	}
	return fetchOrCache(
		'history',
		async () => {
			const userId = iframeDataBridge.getCurrentUser()?.id;
			const claimsPath = userId ? `Core/Scores/Claims?userId=${userId}` : 'Core/Scores/Claims';
			const [claims, deliveryLocations, period, campaigns] = await Promise.all([apiFetch(claimsPath), getDeliveryLocations().catch(() => []), apiFetch('Core/Scores/Periods/Current'), getCampaigns().catch(() => [])]);
			const dlMap = new Map((deliveryLocations ?? []).map((dl) => [dl.id, dl]));
			const campaignsMap = new Map((campaigns ?? []).map((c) => [c.id, c]));
			const allClaims = Array.isArray(claims) ? claims : [];
			const periodClaims = period ? allClaims.filter((c) => c.scorePeriodId === period.id) : allClaims;
			return periodClaims.map((c) => mapClaim(c, dlMap, campaignsMap));
		},
		[]
	);
}

export async function claimPoints({ campaignId, offerId, appointmentUid, deliveryLocationId, pointsClaimed, comment, campaignOfferStartDate, campaignOfferEndDate }) {
	if (!iframeDataBridge.isOnline()) throw new Error('offline');

	const period = await apiFetch('Core/Scores/Periods/Current');
	if (!period) throw new Error('NO_ACTIVE_PERIOD');

	const userId = iframeDataBridge.getCurrentUser()?.id;
	const body = {
		userId,
		scorePeriodId: period.id,
		campaignId: campaignId ?? null,
		campaignOfferId: offerId ?? null,
		appointmentUid: appointmentUid ?? null,
		deliveryLocationId: deliveryLocationId ?? null,
		points: pointsClaimed,
		comment: comment ?? '',
		status: 0,
		campaignOfferStartDate: campaignOfferStartDate ?? null,
		campaignOfferEndDate: campaignOfferEndDate ?? null,
	};

	const response = await authenticatedFetch(getApiUrl('Core/Scores/Claims'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP ${response.status}`);

	await refreshPoints();
	log('success', 'Points', `✅ Claimed ${pointsClaimed} pts`);
	return await response.json();
}

export async function updatePointsClaim({ claimId, pointsClaimed, comment }) {
	try {
		if (!iframeDataBridge.isOnline()) throw new Error('offline');

		const current = await apiFetch(`Core/Scores/Claims/${claimId}`);
		if (!current) throw new Error(`Claim ${claimId} not found`);

		const body = { ...current, points: pointsClaimed, comment: comment ?? '' };

		const response = await authenticatedFetch(getApiUrl('Core/Scores/Claims'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP ${response.status}`);

		await refreshPoints();
		log('success', 'Points', `✅ Updated claim ${claimId} to ${pointsClaimed} pts`);
		return await response.json();
	} catch (error) {
		log('warn', 'Points', `⚠️ updatePointsClaim fallback: ${error.message}`);
		return { success: true, pointsClaimed };
	}
}

export async function refreshPoints() {
	pointsCache.clearMemory();
	await pointsCache.clear();
	// Notify already-mounted consumers (offer recaps, widget badge) that the cache was just
	// invalidated — without this, a zone that fetched history before this claim/update still
	// shows its stale one-shot snapshot forever, since it has no other way to learn data changed.
	window.dispatchEvent(new CustomEvent('points-updated'));
}

registerQueueHandler('POINTS_CLAIM', async (payload) => {
	await claimPoints(payload);
});
